/**
 * SRES · FlowNav — reusable Back / Next / Save / Cancel / Return-to-Dashboard bar.
 * Feb 2026 · Sprint P37y · navigation-simplification pass.
 *
 * The component is DECLARATIVE — every page decides which buttons to show
 * by passing props. The linear "flow order" (Setup → Data → Validation →
 * Analysis → Generate Report → Reports Panel) is defined in
 * `config/simplified_flow.js` and resolved automatically from the current URL.
 *
 * The bar renders in the same visual language as the existing
 * StickyActionBar — no new colours, no new fonts, no new icons.
 */

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FloppyDisk, X, House } from "@phosphor-icons/react";
import { resolveFlowKey, flowNeighbours } from "@/config/simplified_flow";
import { Button } from "@/components/ui/button";

export default function FlowNav({
  onBack,          // custom back handler; if omitted uses flow prev
  onNext,          // custom next handler; if omitted uses flow next
  onSave,          // shows Save button when provided
  onSaveContinue,  // shows Save & Continue button when provided
  onCancel,        // shows Cancel button when provided (default: nav(-1))
  onReturnDashboard, // custom; default navigates to /app/dashboard
  saveLabel = "Save",
  nextLabel,       // override; default uses next milestone name
  backLabel,       // override; default uses previous milestone name
  saving = false,
  nextDisabled = false,
  backDisabled = false,
  hideBack = false,
  hideNext = false,
  hideCancel = false,
  hideReturn = false,
  className = "",
  extraLeft = null,
  extraRight = null,
  compact = false,
}) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { pid } = useParams();

  const currentKey = resolveFlowKey(pathname);
  const { prev, next } = flowNeighbours(currentKey);

  const doBack = () => {
    if (onBack) return onBack();
    if (prev && pid) nav(prev.url(pid));
    else nav(-1);
  };
  const doNext = () => {
    if (onNext) return onNext();
    if (next && pid) nav(next.url(pid));
  };
  const doCancel = () => {
    if (onCancel) return onCancel();
    nav(-1);
  };
  const doReturnDashboard = () => {
    if (onReturnDashboard) return onReturnDashboard();
    nav("/app/dashboard");
  };

  return (
    <div
      className={`shrink-0 border-t border-slate-200 bg-white ${compact ? "px-4 py-2" : "px-6 py-3"} ${className}`}
      data-testid="flow-nav"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2">
        {/* Left: Return to Dashboard + Cancel */}
        {!hideReturn && (
          <Button
            type="button" size="sm" variant="ghost"
            onClick={doReturnDashboard}
            data-testid="flow-return-dashboard"
            className="text-slate-500 hover:text-brand-blue text-[11.5px] gap-1"
          >
            <House size={12} /> Dashboard
          </Button>
        )}
        {!hideCancel && (
          <Button
            type="button" size="sm" variant="ghost"
            onClick={doCancel}
            data-testid="flow-cancel"
            className="text-slate-500 text-[11.5px] gap-1"
          >
            <X size={12} /> Cancel
          </Button>
        )}
        {extraLeft}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right: Back → Save → Save & Continue → Next */}
        {extraRight}
        {!hideBack && (
          <Button
            type="button" size="sm" variant="outline"
            onClick={doBack}
            disabled={backDisabled}
            data-testid="flow-back"
            className="text-[11.5px] gap-1"
          >
            <ArrowLeft size={12} />
            {backLabel || (prev ? `Back · ${prev.label}` : "Back")}
          </Button>
        )}
        {onSave && (
          <Button
            type="button" size="sm" variant="outline"
            onClick={onSave} disabled={saving}
            data-testid="flow-save"
            className="text-[11.5px] gap-1"
          >
            <FloppyDisk size={12} />
            {saving ? "Saving…" : saveLabel}
          </Button>
        )}
        {onSaveContinue && (
          <Button
            type="button" size="sm"
            onClick={onSaveContinue} disabled={saving}
            data-testid="flow-save-continue"
            className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1"
          >
            <FloppyDisk size={12} weight="fill" />
            {saving ? "Saving…" : "Save & Continue"}
          </Button>
        )}
        {!hideNext && !onSaveContinue && (
          <Button
            type="button" size="sm"
            onClick={doNext}
            disabled={nextDisabled || !next}
            data-testid="flow-next"
            className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1"
          >
            {nextLabel || (next ? `Next · ${next.label}` : "Next")}
            <ArrowRight size={12} weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );
}
